<template>
  <div class="services-page">
    <ServiceLandingSection v-if="activeLanding" :landing="activeLanding" />

    <section class="services-header">
      <div>
        <div class="services-tag">MobiFIX PRO</div>
        <h2 class="services-title">
          {{ activeLanding ? `Релевантні послуги для "${activeLanding.h1}"` : 'Послуги ремонту телефонів у Харкові' }}
        </h2>
        <p class="services-subtitle">
          {{ activeLanding ? activeLanding.description : 'Ремонт iPhone, Samsung, Xiaomi та інших смартфонів у Харкові: екрани, батареї, вода, зарядка, камери та плата. Також закриваємо запити ремонт телефона Харьков і ремонт телефонов Харьков.' }}
        </p>
      </div>

      <router-link to="/orders/create" class="services-order-btn">Оформити заявку</router-link>
    </section>

    <section class="services-stats">
      <div class="services-stat">
        <strong>{{ filteredServices.length }}</strong>
        <span>актуальних послуг</span>
      </div>
      <div class="services-stat">
        <strong>від 350 грн</strong>
        <span>базові ремонти</span>
      </div>
      <div class="services-stat">
        <strong>у день звернення</strong>
        <span>типові роботи</span>
      </div>
      <div class="services-stat">
        <strong>до 12 міс.</strong>
        <span>гарантія</span>
      </div>
    </section>

    <section class="services-filters">
      <div class="filter-field">
        <label for="service-category">Категорія</label>
        <select id="service-category" v-model="filters.category">
          <option value="">Усі категорії</option>
          <option value="screen">Заміна екрана</option>
          <option value="battery">Заміна батареї</option>
          <option value="charging">Розʼєм зарядки</option>
          <option value="camera">Камера</option>
          <option value="water-damage">Після води</option>
          <option value="motherboard">Плата</option>
          <option value="software">Програмні несправності</option>
          <option value="other">Інше</option>
        </select>
      </div>

      <div class="filter-field">
        <label for="service-search">Пошук</label>
        <input id="service-search" v-model="filters.search" type="text" placeholder="Наприклад: iPhone 15, батарея, дисплей" />
      </div>

      <div class="filter-field">
        <label for="service-min">Ціна від</label>
        <input id="service-min" v-model.number="filters.minPrice" type="number" min="0" placeholder="0" />
      </div>

      <div class="filter-field">
        <label for="service-max">Ціна до</label>
        <input id="service-max" v-model.number="filters.maxPrice" type="number" min="0" placeholder="10000" />
      </div>
    </section>

    <section v-if="hasActiveFilters" class="services-active">
      <span v-if="filters.category" class="active-chip">{{ categoryLabel(filters.category) }}</span>
      <span v-if="filters.search" class="active-chip">{{ filters.search }}</span>
      <span v-if="filters.minPrice" class="active-chip">від {{ filters.minPrice }} грн</span>
      <span v-if="filters.maxPrice" class="active-chip">до {{ filters.maxPrice }} грн</span>
      <button class="clear-btn" @click="clearFilters">Скинути фільтри</button>
    </section>

    <div v-if="loading" class="services-state">Завантажуємо послуги...</div>
    <div v-else-if="filteredServices.length === 0" class="services-state services-empty">
      За вашим запитом нічого не знайдено. Залиште заявку, і ми підберемо ремонт під вашу модель телефону.
    </div>

    <section v-else class="services-grid">
      <article v-for="service in filteredServices" :key="service._id || service.id || service.name" class="service-card">
        <div class="service-icon">{{ categoryIcon(service.category) }}</div>
        <div class="service-category">{{ categoryLabel(service.category) }}</div>
        <h3>{{ service.name }}</h3>
        <p>{{ service.description }}</p>

        <div v-if="service.compatibility?.length" class="compatibility">
          <span v-for="item in service.compatibility.slice(0, 4)" :key="item" class="compatibility-chip">{{ item }}</span>
        </div>

        <div class="service-meta">
          <span>від {{ service.price }} грн</span>
          <span>{{ service.estimatedTime || 1 }} год</span>
        </div>

        <div class="service-actions">
          <router-link :to="{ name: 'CreateOrder', query: { serviceId: service._id || service.id } }" class="service-btn service-btn-primary">
            Замовити ремонт
          </router-link>
          <a href="tel:+380682772822" class="service-btn service-btn-secondary">Подзвонити</a>
        </div>
      </article>
    </section>

    <section class="services-links-section">
      <div class="services-links-box">
        <div class="services-links-head">
          <h2>{{ activeLanding ? 'Схожі сторінки послуг' : 'Популярні SEO-сторінки послуг' }}</h2>
        </div>
        <div class="services-links-grid">
          <router-link
            v-for="landing in relatedLandings"
            :key="landing.slug"
            :to="landing.path"
            class="mini-link-card"
          >
            <strong>{{ landing.h1 }}</strong>
            <span>{{ landing.keywords[0] }}</span>
          </router-link>
        </div>
      </div>

      <div class="services-links-box">
        <div class="services-links-head">
          <h2>{{ activeLanding ? 'Статті за темою' : 'Статті з блогу' }}</h2>
          <router-link to="/blog" class="services-more-link">Перейти в блог</router-link>
        </div>
        <div class="services-article-list">
          <router-link
            v-for="article in relatedArticles"
            :key="article.slug"
            :to="article.path"
            class="article-link-card"
          >
            <strong>{{ article.h1 }}</strong>
            <p>{{ article.excerpt }}</p>
          </router-link>
        </div>
      </div>
    </section>

    <section class="services-seo-copy">
      <div class="seo-copy-box">
        <h2>{{ activeLanding ? `${activeLanding.h1}: чому звертаються саме до нас` : 'Ремонт телефонів будь-якої моделі у Харкові' }}</h2>
        <p>
          Сторінки послуг побудовані під реальні запити користувачів: ремонт iPhone Харків, ремонт Samsung Харків, заміна батареї Харків,
          терміновий ремонт телефонів Харків, ремонт після залиття Харків, ремонт телефона Харьков і ремонт телефонов Харьков. Це допомагає швидше приводити
          клієнтів із пошуку прямо на потрібний тип ремонту.
        </p>
        <p>
          Якщо ви не знайшли свою модель у списку, все одно залишайте заявку. Ми ремонтуємо Apple, Samsung, Xiaomi, Redmi, POCO, Huawei, Honor,
          OnePlus, Google Pixel та інші пристрої, навіть якщо поломка нестандартна.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import ServiceLandingSection from '../components/ServiceLandingSection.vue';
import { serviceLandings, serviceLandingList } from '../data/serviceLandings';
import { blogArticles } from '../data/blogArticles';
import { applySeo, localBusinessJsonLd } from '../utils/seo';
import { breadcrumbJsonLd, faqJsonLd, reviewJsonLd, serviceJsonLd } from '../utils/schema';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const FALLBACK_SERVICES = [
  { id: 'iphone-screen', name: 'Заміна екрана iPhone', category: 'screen', description: 'Заміна дисплейного модуля для популярних моделей iPhone.', price: 1600, estimatedTime: 1, compatibility: ['iPhone 11', 'iPhone 12', 'iPhone 13', 'iPhone 14'] },
  { id: 'iphone-battery', name: 'Заміна батареї iPhone', category: 'battery', description: 'Повертаємо нормальний час автономної роботи iPhone.', price: 1100, estimatedTime: 0.5, compatibility: ['iPhone 8', 'iPhone X', 'iPhone 11', 'iPhone 12'] },
  { id: 'samsung-screen', name: 'Заміна екрана Samsung Galaxy', category: 'screen', description: 'AMOLED-екрани для популярних моделей Samsung Galaxy.', price: 1800, estimatedTime: 1, compatibility: ['Samsung Galaxy S21', 'Samsung Galaxy S22', 'Samsung Galaxy A54'] },
  { id: 'samsung-battery', name: 'Заміна батареї Samsung', category: 'battery', description: 'Швидка заміна акумулятора для телефонів Samsung.', price: 650, estimatedTime: 0.5, compatibility: ['Samsung Galaxy A53', 'Samsung Galaxy A54', 'Samsung Galaxy S23'] },
  { id: 'xiaomi-screen', name: 'Заміна екрана Xiaomi / Redmi / POCO', category: 'screen', description: 'Ремонт дисплея для популярних моделей Xiaomi, Redmi та POCO.', price: 1100, estimatedTime: 1, compatibility: ['Xiaomi 13', 'Redmi Note 12', 'POCO X5'] },
  { id: 'xiaomi-battery', name: 'Заміна батареї Xiaomi', category: 'battery', description: 'Заміна батареї Xiaomi, Redmi та POCO з гарантією.', price: 450, estimatedTime: 0.5, compatibility: ['Xiaomi 12', 'Redmi Note 12', 'POCO X5'] },
  { id: 'camera', name: 'Ремонт камери телефону', category: 'camera', description: 'Заміна або відновлення основної та фронтальної камери.', price: 800, estimatedTime: 1, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 'charging', name: 'Ремонт розʼєму зарядки', category: 'charging', description: 'Телефон не заряджається, кабель відходить або розʼєм люфтить.', price: 350, estimatedTime: 1, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 'water', name: 'Ремонт телефону після води', category: 'water-damage', description: 'Ультразвукове чищення, діагностика та відновлення після намокання.', price: 600, estimatedTime: 3, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 'board', name: 'Ремонт материнської плати телефону', category: 'motherboard', description: 'Складний ремонт плати, живлення та мікросхем.', price: 1500, estimatedTime: 4, compatibility: ['iPhone', 'Samsung', 'Xiaomi'] },
  { id: 'software', name: 'Відновлення після програмного збою', category: 'software', description: 'Прошивка, скидання, відновлення системи та видалення вірусів.', price: 500, estimatedTime: 1, compatibility: ['Android', 'iOS'] },
  { id: 'speaker', name: 'Ремонт динаміка або мікрофона', category: 'other', description: 'Коли телефон погано чути або вас не чують під час дзвінка.', price: 450, estimatedTime: 1, compatibility: ['iPhone', 'Samsung', 'Xiaomi'] }
];

function getPriceBounds(list) {
  if (!list.length) {
    return { lowPrice: 350, highPrice: 3500 };
  }

  const prices = list.map((item) => Number(item.price) || 0).filter(Boolean);

  if (!prices.length) {
    return { lowPrice: 350, highPrice: 3500 };
  }

  return {
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices)
  };
}

export default {
  name: 'ServicesPage',
  components: {
    ServiceLandingSection
  },
  setup() {
    const featuredLandingSlugs = [
      'remont-telefonov-kharkov',
      'terminovyi-remont-telefoniv-kharkiv',
      'iphone-repair',
      'samsung-repair',
      'screen-replacement',
      'water-damage-repair'
    ];
    const route = useRoute();
    const loading = ref(false);
    const services = ref([]);
    const filters = ref({
      category: '',
      search: '',
      minPrice: null,
      maxPrice: null
    });

    const activeLanding = computed(() => serviceLandings[route.params.slug] || null);
    const sourceServices = computed(() => (services.value.length ? services.value : FALLBACK_SERVICES));

    const landingFilteredServices = computed(() => {
      const landing = activeLanding.value;
      if (!landing) return sourceServices.value;

      return sourceServices.value.filter((service) => {
        const compatibility = (service.compatibility || []).join(' ').toLowerCase();
        const name = String(service.name || '').toLowerCase();
        const description = String(service.description || '').toLowerCase();
        const categoryMatch = landing.category ? service.category === landing.category : true;
        const brandMatch = landing.brandTerms?.length
          ? landing.brandTerms.some((term) => compatibility.includes(term) || name.includes(term) || description.includes(term))
          : true;

        return categoryMatch && brandMatch;
      });
    });

    const filteredServices = computed(() => {
      let list = landingFilteredServices.value;
      const { category, search, minPrice, maxPrice } = filters.value;

      if (category) list = list.filter((service) => service.category === category);

      if (search) {
        const query = search.toLowerCase();
        list = list.filter((service) => {
          const haystack = [service.name, service.description, ...(service.compatibility || [])].join(' ').toLowerCase();
          return haystack.includes(query);
        });
      }

      if (minPrice) list = list.filter((service) => Number(service.price) >= minPrice);
      if (maxPrice) list = list.filter((service) => Number(service.price) <= maxPrice);

      return list;
    });

    const hasActiveFilters = computed(() => Object.values(filters.value).some(Boolean));

    const relatedLandings = computed(() => {
      const landing = activeLanding.value;

      if (!landing) {
        return featuredLandingSlugs.map((slug) => serviceLandings[slug]).filter(Boolean);
      }

      const sameBrand = serviceLandingList.filter((item) => {
        if (item.slug === landing.slug) return false;
        const hasSameCategory = landing.category && item.category === landing.category;
        const hasSameBrand = (landing.brandTerms || []).some((term) => (item.brandTerms || []).includes(term));
        return hasSameCategory || hasSameBrand;
      });

      return (sameBrand.length ? sameBrand : serviceLandingList.filter((item) => item.slug !== landing.slug)).slice(0, 4);
    });

    const relatedArticles = computed(() => {
      const landing = activeLanding.value;

      if (!landing) {
        return blogArticles.slice(0, 4);
      }

      const matches = blogArticles.filter((article) => (article.relatedServices || []).includes(landing.slug));
      return (matches.length ? matches : blogArticles.slice(0, 3));
    });

    const categoryLabel = (category) => ({
      screen: 'Заміна екрана',
      battery: 'Заміна батареї',
      charging: 'Розʼєм зарядки',
      camera: 'Камера',
      'water-damage': 'Після води',
      motherboard: 'Плата',
      software: 'Софт',
      other: 'Інше'
    }[category] || 'Послуга');

    const categoryIcon = (category) => ({
      screen: '📱',
      battery: '🔋',
      charging: '🔌',
      camera: '📷',
      'water-damage': '💧',
      motherboard: '🛠️',
      software: '💻',
      other: '🔧'
    }[category] || '🔧');

    const clearFilters = () => {
      filters.value = {
        category: '',
        search: '',
        minPrice: null,
        maxPrice: null
      };
    };

    async function loadServices() {
      loading.value = true;
      try {
        const response = await axios.get(`${API_URL}/services`);
        services.value = Array.isArray(response.data) ? response.data : [];
      } catch {
        services.value = [];
      } finally {
        loading.value = false;
      }
    }

    function updatePageSeo() {
      const landing = activeLanding.value;

      if (!landing) {
        applySeo({
          title: 'Послуги ремонту телефонів у Харкові',
          description: 'Ремонт iPhone, Samsung, Xiaomi та інших телефонів у Харкові. Екрани, батареї, зарядка, вода, камери та ремонт плати.',
          keywords: [
            'ремонт телефонів Харків',
            'ремонт смартфонів Харків',
            'ремонт телефона Харьков',
            'ремонт телефонов Харьков',
            'заміна батареї Харків',
            'ремонт Samsung Харків',
            'ремонт Xiaomi Харків'
          ],
          path: '/services',
          jsonLd: [
            localBusinessJsonLd(),
            reviewJsonLd(),
            serviceJsonLd({
              name: 'Послуги ремонту телефонів у Харкові',
              description: 'Ремонт телефонів, заміна екрана, батареї, розʼєму зарядки та відновлення після води у Харкові.',
              path: '/services'
            }),
            breadcrumbJsonLd([
              { name: 'Головна', path: '/' },
              { name: 'Послуги', path: '/services' }
            ])
          ]
        });
        return;
      }

      const { lowPrice, highPrice } = getPriceBounds(filteredServices.value.length ? filteredServices.value : landingFilteredServices.value);

      applySeo({
        title: landing.title,
        description: landing.description,
        keywords: landing.keywords,
        path: landing.path,
        jsonLd: [
          localBusinessJsonLd(),
          reviewJsonLd(),
          serviceJsonLd({
            name: landing.h1,
            description: landing.description,
            path: landing.path,
            lowPrice,
            highPrice
          }),
          faqJsonLd(landing.faq),
          breadcrumbJsonLd([
            { name: 'Головна', path: '/' },
            { name: 'Послуги', path: '/services' },
            { name: landing.h1, path: landing.path }
          ])
        ]
      });
    }

    watch(
      () => route.params.slug,
      () => {
        clearFilters();
        updatePageSeo();
      },
      { immediate: true }
    );

    watch(filteredServices, () => {
      updatePageSeo();
    });

    onMounted(loadServices);

    return {
      loading,
      filters,
      activeLanding,
      filteredServices,
      hasActiveFilters,
      relatedLandings,
      relatedArticles,
      categoryLabel,
      categoryIcon,
      clearFilters
    };
  }
};
</script>

<style scoped>
.services-page {
  max-width: 1180px;
  margin: 0 auto;
}

.services-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: end;
  margin-bottom: 20px;
}

.services-tag {
  display: inline-block;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #edf2ff;
  color: #334cb5;
  font-size: 12px;
  font-weight: 800;
}

.services-title {
  margin: 0 0 8px;
  font-size: 34px;
  color: #202868;
  line-height: 1.1;
}

.services-subtitle {
  margin: 0;
  max-width: 760px;
  color: #55607a;
  line-height: 1.7;
  font-size: 16px;
}

.services-order-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  min-height: 52px;
  padding: 12px 20px;
  border-radius: 14px;
  background: linear-gradient(135deg, #534ab7, #7f77dd);
  color: #fff;
  text-decoration: none;
  font-size: 15px;
  font-weight: 800;
}

.services-stats,
.services-filters,
.services-active,
.seo-copy-box,
.service-card,
.services-links-box {
  background: #fff;
  border: 1px solid #dde4ff;
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(46, 44, 97, 0.06);
}

.services-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 18px;
  margin-bottom: 18px;
}

.services-stat {
  text-align: center;
  padding: 8px;
}

.services-stat strong {
  display: block;
  margin-bottom: 6px;
  color: #202868;
  font-size: 22px;
}

.services-stat span {
  color: #5a6682;
  font-size: 13px;
}

.services-filters {
  display: grid;
  grid-template-columns: 1.1fr 1.9fr 1fr 1fr;
  gap: 14px;
  padding: 18px;
  margin-bottom: 14px;
}

.filter-field {
  display: grid;
  gap: 6px;
}

.filter-field label {
  font-size: 12px;
  font-weight: 800;
  color: #55607a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-field input,
.filter-field select {
  min-height: 46px;
  padding: 10px 14px;
  border: 1px solid #d9e0fa;
  border-radius: 12px;
  background: #f7f8ff;
  color: #202868;
  font-size: 14px;
  outline: none;
}

.services-active {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.active-chip {
  padding: 8px 12px;
  border-radius: 999px;
  background: #edf2ff;
  color: #30439a;
  font-size: 13px;
  font-weight: 700;
}

.clear-btn {
  border: 0;
  background: transparent;
  color: #d04545;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.services-state {
  padding: 28px;
  text-align: center;
  color: #5b6480;
  font-size: 16px;
}

.services-empty {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #dde4ff;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.service-card {
  padding: 20px;
  display: grid;
  gap: 10px;
  min-width: 0;
}

.service-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef3ff;
  font-size: 24px;
}

.service-category {
  color: #5064c6;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.service-card h3 {
  margin: 0;
  color: #202868;
  font-size: 20px;
  line-height: 1.2;
}

.service-card p {
  margin: 0;
  color: #5a6682;
  line-height: 1.7;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.compatibility {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.compatibility-chip {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f4f7ff;
  color: #41517c;
  font-size: 12px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.service-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #eef1fb;
  color: #1d9e75;
  font-size: 14px;
  font-weight: 800;
}

.service-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.service-btn {
  min-height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
}

.service-btn-primary {
  background: linear-gradient(135deg, #534ab7, #7f77dd);
  color: #fff;
}

.service-btn-secondary {
  background: #edf5ff;
  color: #245bca;
}

.services-links-section {
  margin-top: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.services-links-box {
  padding: 22px;
}

.services-links-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.services-links-head h2 {
  margin: 0;
  color: #202868;
  font-size: 24px;
}

.services-more-link {
  color: #534ab7;
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
}

.services-links-grid,
.services-article-list {
  display: grid;
  gap: 12px;
}

.mini-link-card,
.article-link-card {
  display: block;
  padding: 16px;
  border-radius: 16px;
  background: #f7f8ff;
  text-decoration: none;
  border: 1px solid #e1e6ff;
  min-width: 0;
}

.mini-link-card strong,
.article-link-card strong {
  display: block;
  margin-bottom: 6px;
  color: #202868;
  font-size: 16px;
}

.mini-link-card span,
.article-link-card p {
  color: #55607a;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.article-link-card p {
  margin: 0;
}

.services-seo-copy {
  margin-top: 24px;
}

.seo-copy-box {
  padding: 24px;
}

.seo-copy-box h2 {
  margin: 0 0 12px;
  color: #202868;
  font-size: 28px;
}

.seo-copy-box p {
  margin: 0 0 12px;
  color: #55607a;
  line-height: 1.75;
}

@media (max-width: 980px) {
  .services-header,
  .services-stats,
  .services-filters,
  .services-grid,
  .services-links-section {
    grid-template-columns: 1fr 1fr;
  }

  .services-header {
    display: grid;
    align-items: stretch;
  }
}

@media (max-width: 720px) {
  .services-title {
    font-size: 28px;
  }

  .services-stats,
  .services-filters,
  .services-grid,
  .services-links-section,
  .service-actions {
    grid-template-columns: 1fr;
  }

  .services-links-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .services-links-box,
  .seo-copy-box,
  .service-card {
    padding: 18px;
  }
}
</style>
